import { DateTimePicker as MuiComponent, DateTimePickerProps as MuiProps } from '@mui/x-date-pickers/DateTimePicker';
import { FieldChangeHandlerContext } from '@mui/x-date-pickers/internals/hooks/useField';
import { DateTimeValidationError } from '@mui/x-date-pickers/models/validation';
import { type ConfigType, Dayjs } from 'dayjs';
import React, { useCallback } from 'react';
import { BpDesignSystem } from '../../types';
import { dayjs } from '../../utils';
import { Input, InputProps } from '../Form/Input';
import { getHelperText } from '../Form/HelperText';

export type DateTimePickerContext = FieldChangeHandlerContext<DateTimeValidationError>;

export interface DateTimePickerProps
  extends Omit<MuiProps<Dayjs>, 'minDate' | 'maxDate'>,
    Pick<InputProps, 'subLabel' | 'helperText' | 'errorText'> {
  dataTest?: string;
  id?: string;
  clearable?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  minDate?: ConfigType;
  maxDate?: ConfigType;
}

export const DateTimePicker = ({
  value,
  onChange,
  disabled = false,
  clearable = true,
  dataTest = 'data-time-picker',
  className,
  style,
  sx,
  error,
  slots,
  slotProps,
  fullWidth,
  label,
  subLabel,
  helperText,
  errorText,
  id,
  placeholder,
  minDate,
  maxDate,
  ...rest
}: BpDesignSystem.InputProps<DateTimePickerProps>) => {
  const targetMinDate = minDate ? dayjs(minDate) : undefined;
  const targetMaxDate = maxDate ? dayjs(maxDate) : undefined;

  const setDate = useCallback(
    (date: Dayjs | null, context?: DateTimePickerContext) => {
      if (date && date.isValid()) {
        onChange?.(
          date,
          context ?? {
            validationError: null
          }
        );
      } else if (clearable) {
        onChange?.(null, {
          validationError: null
        });
      }
    },
    [onChange, clearable]
  );

  return (
    <MuiComponent
      minDate={targetMinDate}
      maxDate={targetMaxDate}
      slots={{
        ...slots,
        textField: Input
      }}
      slotProps={{
        ...slotProps,
        field: {
          clearable: !!clearable,
          onClear: () => setDate(null),
          ...slotProps?.field
        },
        textField: {
          'data-test': dataTest,
          error,
          sx,
          style,
          fullWidth,
          label,
          subLabel,
          id,
          placeholder,
          onChange: setDate,
          helperText: !!helperText || !!errorText ? getHelperText(error, helperText, errorText) : undefined,
          ...(slotProps?.textField ?? {})
        } as unknown as InputProps
      }}
      onAccept={setDate}
      {...rest}
      className={`date-time-picker ${className || ''}`}
    />
  );
};
